import { DocumentType, OrderType } from '@innedit/innedit-type';
import { OrderData } from 'packages/innedit';
import React, { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  change,
  DataFieldProps,
  Field,
  WrappedFieldProps,
} from '../../packages/formidable';
import ComponentField from '../../styles/Field';

interface DataOrdersSelectProps extends Omit<DataFieldProps, 'componentType'> {
  espaceId: string;
  formName: string;
}

const DataOrdersSelectRender: FC<WrappedFieldProps & DataOrdersSelectProps> = ({
  espaceId,
  formName,
  input,
  label,
  meta,
}) => {
  const [options, setOptions] = useState<DocumentType<OrderType>[]>();
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    const current = selectRef && selectRef.current;

    const handleKeyup = () => {
      if (current) {
        if (0 === current.value.length) {
          current.classList.add('is-empty');
        } else {
          current.classList.remove('is-empty');
        }
      }
    };

    const commandeData = new OrderData({ espaceId });
    const unsub = commandeData.watch(newDocs => {
      setOptions(newDocs);

      if (current) {
        if (current.value) {
          current.classList.remove('is-empty');
        } else {
          current.classList.add('is-empty');
        }

        current.addEventListener('focus', () => {
          current.classList.add('is-focused');
        });
        current.addEventListener('blur', () => {
          current.classList.remove('is-focused');
        });
        current.addEventListener('keyup', handleKeyup);
      }
    });

    return () => {
      if (unsub) {
        unsub();
      }

      if (current) {
        current.removeEventListener('keyup', handleKeyup);
      }
    };
  }, [espaceId]);

  const handleOnChange = (event: SyntheticEvent<HTMLSelectElement>) => {
    const { name } = input;
    const { dispatch } = meta;

    if (options) {
      dispatch(change(formName, name, event.currentTarget.value));
    }
  };

  const { name, value } = input;
  const { touched, error } = meta;

  const id = uuidv4();

  return (
    <ComponentField
      className={`field select ${touched && error ? 'error ' : ''}`}
    >
      {options && options.length > 0 ? (
        <select
          ref={selectRef}
          id={id}
          name={name}
          onChange={handleOnChange}
          value={value}
        >
          <option value="">Non spécifié</option>
          {options.map(item => (
            <option key={`${name}_${item.id}`} value={item.id}>
              {item.reference
                ? `#${item.reference} par ${item.contactName}`
                : item.id}
            </option>
          ))}
        </select>
      ) : (
        <div>...</div>
      )}
      <label className="label" htmlFor={id}>
        <span>{label}</span>
      </label>
      {touched && error && (
        <div className={`error error_${error.code}`}>
          <span className="icon">
            <i className="fa fa-exclamation" />
          </span>
          <span>{error.message}</span>
        </div>
      )}
    </ComponentField>
  );
};

const DataOrdersSelect: FC<DataOrdersSelectProps> = props => (
  <Field
    {...props}
    component={DataOrdersSelectRender as any}
    name={props.name}
  />
);

export default DataOrdersSelect;
